import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, Row, Col, Table, Form } from 'react-bootstrap';
import { FieldArray, FormikContextType, useFormikContext } from 'formik';
import Plus from 'mdi-react/PlusIcon';
import classNames from 'classnames';
import Minus from 'mdi-react/CloseIcon';
import { findLastIndex } from 'lodash';
import AlertCircleOutline from 'mdi-react/AlertCircleOutlineIcon';

import { errorReqSelector } from 'modules/common/FillTrip';
import { getLocationReguest } from 'modules/ordering/containers/TripContainer/services/actions';
import { locationSel } from 'modules/ordering/containers/TripContainer/services/selectors';
import { commonTripSelector } from 'modules/common/CommonTrip/selectors';
import { RolesMode } from 'entities/Roles';
import { useModal } from '../hooks';

import { Input, Checkbox, Select } from '../Forms';
import ModalWrapper from '../ModalWrapper';
import styles from './RegOfDocuments.module.scss';
import { deleteOrderFromTripRequest } from '../../common/CommonTrip/actions';

type Props = {
  handleChange: (e: React.ChangeEvent<any>) => void;
  setFieldValue: (name: string, value: any) => void;
  mode: RolesMode;
};

type RegDocument = {
  businessPartnerCode: string;
  id: number;
  isDeleted: boolean;
  isInputError: boolean;
  isOrderChanged: boolean;
  locationId: number;
  netWeight: number;
  number: string;
  orderHand: number;
  orderObject: string;
  location: any;
};

const MAX_LINES = 30;

const RegOfDocuments: React.FC<Props> = ({ handleChange, setFieldValue, mode }) => {
  const dispatch = useDispatch();
  const formik = useFormikContext();
  const { values, errors }: FormikContextType<any> = formik;

  const [removeIndex, setRemoveIndex] = useState<number>(0);
  const [ordersLine, setOrdersLine] = useState<number>(0);
  // const [onPrint, setOnPrint] = useState<boolean>(false);
  const locations = useSelector(locationSel);
  // const dataOrders = useSelector(tripOrdersSel);
  const { trip } = useSelector(commonTripSelector);
  const errorReqDoc = useSelector(errorReqSelector);
  const { isShow, openModal, closeModal } = useModal();
  const { orders } = values;
  const optNumbers = [...new Array(orders.filter((o: any) => !o.isDeleted && !o.isInputError).length)];
  // const isDisabled = !orders.find((r: any) => r.number);
  const isDirector = mode === 'director';
  const isOrdering = mode === 'ordering';

  const orderManualyHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;

    orders.forEach((doc: RegDocument, index: number) => {
      if (+doc.orderHand === +value) {
        setFieldValue(`orders[${index}].orderHand`, 0);
      }
    });
    handleChange(e);
  };

  const deleteReasonHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id } = e.target;
    if (id === 'isDeleted') {
      setFieldValue(`orders[${removeIndex}].isDeleted`, true);
      setFieldValue(`orders[${removeIndex}].isInputError`, false);
    }

    if (id === 'isInputError') {
      setFieldValue(`orders[${removeIndex}].isDeleted`, false);
      setFieldValue(`orders[${removeIndex}].isInputError`, true);
    }

    handleChange(e);
  };

  useEffect(() => {
    if (orders) {
      orders.forEach((doc: RegDocument, i: number) => {
        if (orders.find((o: RegDocument) => o.id !== doc.id && +o.orderHand === +doc.orderHand)) {
          setFieldValue(`orders[${i}].orderHand`, 0);
        }
        if (orders[i].isOrderChanged) setFieldValue(`orders[${i}].isOrderChanged`, true);
      });
    }
    if (!locations) dispatch(getLocationReguest());
    // Trigger validation when orders change to ensure button disable state updates immediately
    formik.validateForm();
  }, [orders.length, orders.map((o: any) => o.number).join()]);

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex align-items-center py-0">
        {errors.orders && (
          <span className="d-flex align-items-center justify-content-center error-circle">
            <AlertCircleOutline size={15} className="text-danger" />
          </span>
        )}
        <span className="d-block font-weight-bolder ml-4 mr-5 py-1">Реєстрація документів</span>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col>
            <FieldArray
              name="orders"
              render={(arrayHelpers) => {
                const addHandler = (e: any, latterOrderPartNum: boolean | undefined) => {
                  const businessPartnerCode = orders.length ? orders[orders.length - 1].businessPartnerCode : '';
                  arrayHelpers.push({
                    number: '',
                    orderObject: '',
                    businessPartnerCode,
                    isOrderChanged: false,
                    locationId: '',
                    orderHand: null,
                    latterOrderPartNum,
                  });
                  setOrdersLine((prev) => prev + 1);
                };

                const deleteHandler = () => {
                  dispatch(deleteOrderFromTripRequest(values?.orders[removeIndex], values, removeIndex));
                  closeModal();
                  // setOrdersLine((prev) => prev - 1);
                  setRemoveIndex(0);
                };

                const removeHandler = (index: number) => arrayHelpers.remove(index);

                return (
                  <>
                    <Table className={styles.table}>
                      <thead>
                        <tr className={styles.headerRow}>
                          <th
                            className={classNames(
                              'd-flex align-items-center',
                              styles.wideCell,
                              styles.activeTRow,
                              styles.orderObject,
                            )}
                          >
                            {ordersLine < MAX_LINES && !isDirector && (
                              <Button
                                variant="success"
                                className="d-flex justify-content-center align-items-center mr-2 p-1 rounded-circle"
                                onClick={(e) => addHandler(e, false)}
                                disabled={
                                  values?.orders?.length !== 0 && // @ts-ignore
                                  values?.orders[findLastIndex(values?.orders)]?.number === ''
                                }
                              >
                                <Plus />
                              </Button>
                            )}
                            Замовлення:&nbsp;<em className="text-danger">*</em>
                          </th>
                          <th
                            className={classNames(
                              'd-flex align-items-center',
                              styles.wideCell,
                              styles.activeTRow,
                              styles.orderObject,
                            )}
                          >
                            {ordersLine < MAX_LINES && isOrdering && trip?.status?.id === 5 && (
                              <Button
                                variant="warning"
                                className={classNames('mr-2 p-1 rounded-circle')}
                                onClick={(e) => addHandler(e, true)}
                              >
                                <Plus />
                              </Button>
                            )}
                            Номер наряда:&nbsp;<em className="text-danger">*</em>
                          </th>
                          <th className={styles.iddp}>Код діл-го партнера:</th>
                          <th className={styles.locationId}>
                            Локація:&nbsp;<em className="text-danger">*</em>
                          </th>
                          <th className={styles.isOrderChangedHead}>Зміни в наряді:</th>
                          <th className={styles.orderHand}>
                            Порядок вручну:&nbsp;<em className="text-danger">*</em>
                          </th>
                          {/* eslint-disable-next-line */}
                          <th className={styles.textCell}>Повідомлення:</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* eslint-disable-next-line */}
                        {orders?.map((value: any, index: number) => {
                          const openDialog = () => {
                            // if (dataOrders && dataOrders.find((o: any) => o.number === value.number)) {
                            //   setFieldValue(`orders[${index}].isDeleted`, true);
                            //   setFieldValue(`orders[${index}].isInputError`, false);
                            // }
                            setRemoveIndex(index);
                            openModal();
                          };

                          return (
                            // eslint-disable-next-line
                            <tr className={styles.bodyRow} key={index}>
                              <td className={styles.orderObject}>
                                <Input
                                  className="w-100"
                                  name={`orders[${index}].orderObject`}
                                  label=""
                                  onChange={handleChange}
                                  requestParamName="orders"
                                  required
                                  onLine
                                  disabled={isDirector || value?.number || value?.latterOrderPartNum}
                                  isDisabledBtnDots={
                                    values?.orders[index]?.orderObject === '' ||
                                    values?.orders[index]?.number ||
                                    values?.orders[index]?.orderObject === values?.orders[index - 1]?.orderObject
                                  }
                                />
                              </td>
                              <td
                                className={classNames(
                                  'position-relative d-flex align-items-center',
                                  styles.cell,
                                  styles.wideCell,
                                  styles.number,
                                )}
                              >
                                <Input
                                  name={`orders[${index}].number`}
                                  requestParamName={orders[index]?.latterOrderPartNum ? 'orderPartNum' : undefined}
                                  label=""
                                  className="w-100"
                                  maxLength={10}
                                  value={value.number}
                                  onChange={handleChange}
                                  disabled={!orders[index]?.latterOrderPartNum}
                                  isDisabledBtnDots={
                                    orders.filter(
                                      (x: any, i: number) =>
                                        i !== index &&
                                        (x.number === orders[index]?.number ||
                                          x.number === orders[index]?.number.replace(/^0+/, '')),
                                    ).length > 0
                                  }
                                  required
                                  onLine
                                />
                                {!isDirector && (
                                  <Button
                                    variant="danger"
                                    className="d-flex justify-content-center align-items-center ml-2 p-1 rounded-circle"
                                    onClick={() => {
                                      if (orders[index].number === '') {
                                        removeHandler(index);
                                      } else {
                                        setFieldValue(`orders[${index}].isDeleted`, true);
                                        setFieldValue(`orders[${index}].isInputError`, false);
                                        openDialog();
                                      }
                                    }}
                                  >
                                    <Minus />
                                  </Button>
                                )}
                              </td>
                              <td className={styles.iddp}>
                                <Input
                                  type="number"
                                  name={`orders[${index}].company.iddp`}
                                  className={styles.iddpField}
                                  label=""
                                  onChange={handleChange}
                                  onLine
                                  step="1"
                                  pattern="\d+"
                                  min={0}
                                  maxLength={15}
                                  disabled
                                />
                              </td>
                              <td className={styles.locationId}>
                                <Select
                                  name={`orders[${index}].locationId`}
                                  label=""
                                  onChange={handleChange}
                                  required
                                  onLine
                                  disabled
                                >
                                  {locations
                                    ? locations.map((l) => (
                                        <option key={`local_${l.id}`} value={l.id}>
                                          {l.name}
                                        </option>
                                      ))
                                    : null}
                                </Select>
                              </td>
                              <td className={classNames(styles.isOrderChanged, 'd-flex justify-content-center')}>
                                <Checkbox
                                  name={`orders[${index}].isOrderChanged`}
                                  label=""
                                  onChange={handleChange}
                                  disabled={isDirector}
                                />
                              </td>
                              <td className={styles.orderHand}>
                                <Select
                                  name={`orders[${index}].orderHand`}
                                  label=""
                                  onChange={orderManualyHandler}
                                  onLine
                                  disabled={isDirector}
                                >
                                  {/* eslint-disable-next-line */}
                                  {optNumbers.map((val, idx) => (
                                    <option key={idx} value={idx + 1}>
                                      {idx + 1}
                                    </option>
                                  ))}
                                </Select>
                              </td>
                              {value?.reservationMessage ? (
                                <td className={styles.textCell}>
                                  <small className="font-weight-bolder m-0 text-danger">
                                    {value?.reservationMessage}
                                  </small>
                                </td>
                              ) : (
                                <td className={styles.textCell} />
                              )}
                              {errorReqDoc && index === 0 && (
                                <td className={styles.textCell}>
                                  <small className="font-weight-bolder m-0 text-danger">
                                    Функція автозаповнення наразі недоступна, очікується інтеграція з SAP
                                  </small>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                    <ModalWrapper
                      show={isShow}
                      onHide={() => {
                        setRemoveIndex(0);
                        setFieldValue(`orders[${removeIndex}].isDeleted`, false);
                        setFieldValue(`orders[${removeIndex}].isInputError`, false);
                        closeModal();
                      }}
                    >
                      <div className="d-flex flex-column">
                        <h6 className="text-center">Вкажіть причину видалення наряду:</h6>
                        <Form.Check
                          className="mb-2"
                          type="radio"
                          label="Наряд видалено"
                          name="delReason"
                          // @ts-ignore
                          onChange={deleteReasonHandler}
                          id="isDeleted"
                          defaultChecked
                          inline
                        />
                        <Form.Check
                          type="radio"
                          label="Помилка вводу"
                          name="delReason"
                          // @ts-ignore
                          onChange={deleteReasonHandler}
                          id="isInputError"
                          inline
                        />
                        <div className="d-flex justify-content-end mt-3">
                          <Button className="px-5" onClick={deleteHandler} variant="outline-secondary">
                            Підтвердити
                          </Button>
                        </div>
                      </div>
                    </ModalWrapper>
                  </>
                );
              }}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col className="d-flex align-items-center">
            <div className={styles.lOffset}>
              <Checkbox
                name="isHighPriority"
                label="Високий пріоритет погрузки"
                // @ts-ignore
                value={false}
                disabled={isDirector}
                className="col-4 _req-checkbox"
                onChange={handleChange}
              />
            </div>
          </Col>
          {/* <Col className="d-flex justify-content-end">
            <Button
              className={classNames('px-3', styles.btn)}
              disabled={isDisabled}
              variant="outline-secondary"
              onClick={() => setOnPrint(true)}
            >
              Надрукувати штрихкод
            </Button>

            {values.orders && onPrint && (
              <PrintCodeContainer onClose={() => setOnPrint(false)} values={values.orders} />
            )}
          </Col> */}
        </Row>
        <Row className="mb-3">
          <Col className="d-flex align-items-center">
            <span>Тип перевізника:</span>
            <em className={classNames('text-danger m-1')}>*</em>
            <div className={styles.lOffset}>
              <fieldset disabled={!(trip?.status?.id === 2 || trip?.status?.id === 1 || trip?.status?.id === 21)}>
                <Select
                  name="сarrierType"
                  onChange={(e) => {
                    setFieldValue('isHighPriority', Number(e.target.value) === 0);
                    handleChange(e);
                  }}
                  onLine
                >
                  <option key={0} value={0}>
                    кінцевий споживач
                  </option>
                  <option key={1} value={1}>
                    авто МІШ
                  </option>
                  <option key={2} value={2}>
                    авто СМЦ
                  </option>
                  <option key={3} value={3}>
                    eкспорт
                  </option>
                </Select>
              </fieldset>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default RegOfDocuments;
